import client from '@/shared/api/client';
import { isMockMode } from '@/__dev__/isMockMode';
import {
  mockListUserInvites,
  mockListUserInvitesPaginated,
  mockCreateUserInvite,
  mockCreateUserInvites,
  mockCancelUserInvite,
  mockResendUserInvite,
} from '@/__dev__/mocks/userInvites.mock';

import type {
  CreateInviteRequest,
  CreateInvitesRequest,
  InviteItem,
  InviteListResponse,
  InvitationsListResponse,
  RawInvitationsListResponse,
  RawApiInviteItem,
  ListInvitationsParams,
} from '@/features/users/types/users.types';

/**
 * User Invites Service
 *
 * API client for invited-users management.
 *
 * @module features/users/services/userInvites
 */

/**
 * Maps a raw API invite to the normalised `InviteItem` used by the UI.
 *
 * | API field        | InviteItem field |
 * |------------------|------------------|
 * | `inviteId`       | `id`             |
 * | `expirationDate` | `expiresAt`      |
 * | `ACCEPTED`       | `REGISTERED`     |
 */
export function mapRawInvite(raw: RawApiInviteItem): InviteItem {
  return {
    id: raw.inviteId,
    email: raw.email,
    fullName: raw.fullName,
    status: raw.status === 'ACCEPTED' ? 'REGISTERED' : raw.status,
    expiresAt: raw.expirationDate,
  };
}

/**
 * Lists sent invitations (paginated)
 *
 * @param params - Pagination parameters
 * @returns Promise with paginated invitations list
 *
 * @example
 * ```typescript
 * const invites = await listUserInvites({ offset: 0, limit: 10 });
 * console.log(invites.content); // Array of invites
 * ```
 */
export async function listUserInvites(
  params: ListInvitationsParams = {}
): Promise<InviteListResponse> {
  if (isMockMode) return mockListUserInvites(params);
  const { offset = 0, limit = 10 } = params;
  const { data } = await client.get<RawInvitationsListResponse>(
    '/users/invitations',
    {
      params: { offset, limit },
    }
  );

  // Transform to legacy format for backward compatibility
  return {
    items: data.content.map(mapRawInvite),
    total: data.page.totalElements,
  };
}

/**
 * Lists sent invitations with full pagination info
 *
 * @param params - Pagination parameters
 * @returns Promise with paginated response including links
 */
export async function listUserInvitesPaginated(
  params: ListInvitationsParams = {}
): Promise<InvitationsListResponse> {
  if (isMockMode) return mockListUserInvitesPaginated(params);
  const { offset = 0, limit = 10, ...filters } = params;
  const { data } = await client.get<RawInvitationsListResponse>(
    '/users/invitations',
    {
      params: { offset, limit, ...filters },
    }
  );
  return {
    ...data,
    content: data.content.map(mapRawInvite),
  };
}

/**
 * Creates a single invitation
 *
 * @param payload - Email and optional fullName
 * @returns Promise that resolves when the invitation is sent
 *
 * @example
 * ```typescript
 * await createUserInvite({ email: 'user@email.com', fullName: 'User Name' });
 * ```
 */
export async function createUserInvite(
  payload: CreateInviteRequest
): Promise<void> {
  if (isMockMode) return mockCreateUserInvite(payload);
  await client.post('/users/invite', [payload]);
}

/**
 * Creates multiple invitations at once
 *
 * @param payload - Array of email and fullName pairs
 * @returns Promise that resolves on success
 *
 * @example
 * ```typescript
 * await createUserInvites([
 *   { email: 'user1@email.com', fullName: 'User One' },
 *   { email: 'user2@email.com', fullName: 'User Two' }
 * ]);
 * ```
 */
export async function createUserInvites(
  payload: CreateInvitesRequest
): Promise<void> {
  if (isMockMode) return mockCreateUserInvites(payload);
  await client.post('/users/invite', payload);
}

/**
 * Cancels/removes a pending invitation
 *
 * Only invitations with status PENDING can be removed.
 *
 * @param inviteId - ID of the invitation to remove
 * @returns Promise that resolves on success (204 No Content)
 *
 * @throws Error if invite is not found or not pending (404)
 */
export async function cancelUserInvite(inviteId: string): Promise<void> {
  if (isMockMode) return mockCancelUserInvite(inviteId);
  await client.delete(`/users/invitations/${inviteId}`);
}

/**
 * Resends an expired invitation.
 *
 * The API has no dedicated resend endpoint, so this operation
 * cancels the existing invite (`DELETE`) and creates a brand-new one
 * (`POST /users/invite`) with the same email and name.
 *
 * @param invite - The invite to resend (must include `id`, `email` and optionally `fullName`)
 * @returns Promise that resolves when the new invitation is sent
 */
export async function resendUserInvite(
  invite: Pick<InviteItem, 'id' | 'email' | 'fullName'>
): Promise<void> {
  if (isMockMode) return mockResendUserInvite(invite);

  await client.post('/users/invite', [
    {
      email: invite.email,
      fullName: invite.fullName,
    },
  ]);
}
